import { Auth } from 'aws-amplify'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Seo } from '../components'
import { Heading } from '../components/ui/Heading'
import { useNotificationContext, useUserContext } from '../context'
import MainContainer from '../features/discover/components/MainContainer'
import { notifyBugsnag } from '../helpers'

export default function ConfirmEmail() {
  const [code, setCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)
  const [limitExceeded, setLimitExceeded] = useState(false)

  const { cognitoUser } = useUserContext()
  const { success, error } = useNotificationContext()
  const { t } = useTranslation()

  const { email, email_verified } = cognitoUser?.attributes || {}

  // Initial Code anfordern falls nicht verifiziert
  useEffect(() => {
    if (email_verified) return
    Auth.currentAuthenticatedUser()
      .then((user) =>
        Auth.verifyUserAttribute(user, 'email').then(() => setSeconds(60))
      )
      .catch((err) => {
        notifyBugsnag(err)
        error(t('confirmEmailPage.error.generic'))
      })
  }, [email_verified, error, t])

  // Countdown
  useEffect(() => {
    if (seconds <= 0) return
    const id = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0))
    }, 1000)
    return () => clearInterval(id)
  }, [seconds])

  const handleConfirm = async (e) => {
    e.preventDefault()
    if (!code) return
    setIsVerifying(true)
    setErrorMessage(null)
    try {
      const res = await Auth.verifyCurrentUserAttributeSubmit(
        'email',
        code.trim()
      )
      if (res === 'SUCCESS') {
        success(t('confirmEmailPage.success'))
        // Hard refresh so app state rehydrates with verified email
        setTimeout(() => window.location.replace('/'), 1200)
      } else {
        setErrorMessage(t('confirmEmailPage.error.invalid_code'))
      }
    } catch (err) {
      notifyBugsnag(err)
      switch (err.code) {
        case 'CodeMismatchException':
          setErrorMessage(t('confirmEmailPage.error.invalid_code'))
          setCode('')
          break
        case 'ExpiredCodeException':
          setErrorMessage(t('confirmEmailPage.error.expired_code'))
          break
        case 'LimitExceededException':
          setLimitExceeded(true)
          setErrorMessage(t('confirmEmailPage.error.limit_exceeded_exception'))
          break
        default:
          setErrorMessage(t('confirmEmailPage.error.generic'))
          break
      }
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResend = useCallback(async () => {
    if (seconds > 0 || isResending || limitExceeded) return
    setIsResending(true)
    setErrorMessage(null)
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.verifyUserAttribute(user, 'email')
      setSeconds(30)
      setCode('')
      success(t('confirmEmailPage.resend.success'))
    } catch (err) {
      notifyBugsnag(err)
      if (err.code === 'LimitExceededException') {
        setLimitExceeded(true)
        setErrorMessage(t('confirmEmailPage.error.limit_exceeded_exception'))
      } else {
        setErrorMessage(t('confirmEmailPage.error.generic'))
      }
      error(t('confirmEmailPage.resend.error'))
    } finally {
      setIsResending(false)
    }
  }, [seconds, isResending, limitExceeded, success, error, t])

  return (
    <div className='min-h-screen w-full' style={{ backgroundColor: '#EBEFFD' }}>
      <Seo title={t('confirmEmailPage.title')} />
      <div className='h-screen overflow-y-auto'>
        <MainContainer>
          <div className='flex max-w-[54rem] flex-col'>
            <Heading level={2} className='text-darkBlue'>
              {t('confirmEmailPage.title')}
            </Heading>

            <form
              onSubmit={handleConfirm}
              className='mt-6 flex flex-col gap-y-5'
            >
              <div>
                <label
                  htmlFor='email'
                  className='block text-sm/6 font-medium text-darkBlack'
                >
                  {t('confirmEmailPage.email_input.label')}
                </label>
                <div className='mt-2'>
                  <input
                    id='email'
                    type='email'
                    disabled
                    value={email || ''}
                    className='block w-full rounded-md border-softGrey bg-white px-3 py-1.5 text-darkBlack outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-primaryBlue disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6'
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor='confirmationCode'
                  className='block text-sm/6 font-medium text-darkBlack'
                >
                  {t('confirmEmailPage.code_input.label')}
                </label>
                <div className='mt-2'>
                  <input
                    id='confirmationCode'
                    name='confirmationCode'
                    type='text'
                    inputMode='numeric'
                    autoComplete='one-time-code'
                    placeholder={t('confirmEmailPage.code_input.placeholder')}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    aria-invalid={!!errorMessage}
                    aria-describedby='confirmationCode-description'
                    className='block w-full rounded-md border-softGrey bg-white px-3 py-1.5 text-base text-darkBlack outline-1 -outline-offset-1 outline-softGrey placeholder:text-gray-400 focus:outline-2 focus:outline-primaryBlue sm:text-sm/6'
                  />
                </div>
                <p
                  id='confirmationCode-description'
                  className='mt-2 text-sm text-gray-500'
                >
                  {t('confirmEmailPage.code_input.description')}
                </p>
              </div>

              {errorMessage && (
                <div
                  role='alert'
                  className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200'
                >
                  {errorMessage}
                </div>
              )}

              <div className='flex flex-row items-center gap-x-6'>
                <button
                  type='submit'
                  disabled={isVerifying || !code || limitExceeded}
                  className='flex h-9 items-center justify-center rounded-full bg-primaryBlue px-5 text-sm font-medium text-white disabled:opacity-50'
                >
                  {isVerifying
                    ? t('confirmEmailPage.cta_confirm_loading')
                    : t('confirmEmailPage.cta_confirm')}
                </button>

                {seconds > 0 ? (
                  <span className='text-sm text-gray-600'>
                    {t('confirmEmailPage.resend.wait', { seconds })}
                  </span>
                ) : (
                  <button
                    type='button'
                    onClick={handleResend}
                    disabled={isResending || limitExceeded}
                    className='text-sm font-medium text-buttonSecondaryBlue disabled:opacity-40'
                  >
                    {isResending
                      ? t('confirmEmailPage.resend.loading')
                      : t('confirmEmailPage.resend.action')}
                  </button>
                )}
              </div>

              {limitExceeded && (
                <p className='text-xs text-red-600'>
                  {t('confirmEmailPage.error.limit_exceeded_exception')}
                </p>
              )}
            </form>
          </div>
        </MainContainer>
      </div>
    </div>
  )
}
